<template>
  <div v-rect-loading="loading" class="mobile-container">
    <div class="mobile-editor-header">
      <div class="mobile-editor-title">{{ $t(`${i18nPrefix}.title.${mobileInfo.currentMobile ? 'changeMobileRequest' : 'bindMobileRequest'}`) }}</div>
    </div>
    <div class="mobile-info-container">
      <WrappedFrom
        hideRequiredAsterisk
        ref="form"
        class="mobile-info"
        :model="mobileInfo"
        :formLines="formLines"
      >
        <template #smsValidationCode>
          <div class="form-line" :class="getErrorClass(message.verifyCode)">
            <PhoneVerifyCodeInput
              v-if="isVerifyCodeComponentActive"
              v-model="mobileInfo.smsValidationCode"
              class="line-input"
              :captchaIndex="3"
              :phone="sendValidateCode"
              :placeholder="$t('login.common.label.verifyCode')"
              @focus="clearErrorMessage(message.verifyCode)"
              @blur="validate(message.verifyCode)"
              @change="validate(message.verifyCode)"
              @sendVerifySuccess="handelSendVerifySuccess"
            />
            <div v-show="message.verifyCode.show" class="line-tip">{{ selectLabelI18n(message.verifyCode, 'text') }}</div>
          </div>
        </template>
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="save-btn" type="primary" @click="handleSave">{{ $t('common.btn.submit') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { selectLabelI18n } from '@/i18n'
import { mapActions } from 'vuex'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import { validatePhone } from '@/utils/validator'
import PhoneVerifyCodeInput from '@/components/formEditor/phoneVerifyCodeInput.vue'

export default {
  components: {
    WrappedFrom,
    PhoneVerifyCodeInput
  },
  props: {
  },
  data () {
    return {
      i18nPrefix: 'personal.functions.changeMobile',
      loading: false,
      selectLabelI18n,
      isVerifyCodeComponentActive: true,
      personalInfo: null,
      mobileInfo: {
        currentMobile: null,
        mobile: null,
        smsValidationCode: null
      },
      hasSentVerifyCode: false,
      message: {
        verifyCode: {
          show: false,
          isError: false,
          text: null,
          textI18nKey: null
        }
      }
    }
  },
  computed: {
    formLines () {
      const lines = {
        currentMobile: {
          label: this.$t(`${this.i18nPrefix}.label.currentMobile`),
          disabled: true,
          hidden: !this.mobileInfo.currentMobile
        },
        mobile: {
          label: this.$t(`${this.i18nPrefix}.label.newMobile`),
          placeholder: this.$t(`${this.i18nPrefix}.label.pleaseEnterMobile`),
          rules: [
            { required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' },
            { validator: this.validateChinaPhone, trigger: 'blur' }
          ]
        },
        smsValidationCode: {
          label: this.$t(`${this.i18nPrefix}.label.smsCode`),
          contentSlot: 'smsValidationCode'
        }
      }

      return lines
    },
    sendValidateCode () {
      const error = validatePhone(null, this.mobileInfo.mobile)
      if (error || this.mobileInfo.currentMobile === this.mobileInfo.mobile) {
        return null
      }

      return this.mobileInfo.mobile
    },
    companyId () {
      return this.$route.query.company_id
    }
  },
  created () {
    this.loadPersonalInfo()
  },
  methods: {
    ...mapActions('account', ['updateAccountMobile']),
    ...mapActions('personal', ['getPersonalInfo']),
    async loadPersonalInfo () {
      this.loading = true
      try {
        const resp = await this.getPersonalInfo({ company_id: this.companyId })
        if (resp) {
          this.personalInfo = resp
          this.mobileInfo.currentMobile = this.personalInfo.mobile
        }
      } finally {
        this.loading = false
      }
    },
    getErrorClass (message) {
      return message && message.show && message.isError && 'has-error'
    },
    setErrorMessage (message, textI18nKey, getter) {
      if (getter) {
        Object.defineProperty(message, 'text', {
          configurable: true,
          enumerable: true,
          get: getter
        })
      } else {
        message.text = null
      }
      message.isError = true
      message.textI18nKey = textI18nKey
      message.show = true
    },
    clearErrorMessage (message) {
      message.show = false
      message.isError = false
      message.text = null
      message.textI18nKey = null
    },
    validate (message) {
      if (!message) {
        return _.chain(this.message).map(this.validate).includes(false).value()
      }
      let hasError = false
      let textI18nKey = null
      const getter = null
      if (message === this.message.verifyCode) {
        if (this.hasSentVerifyCode) {
          if (this.mobileInfo.smsValidationCode && !/^\d{0,6}$/.test(this.mobileInfo.smsValidationCode)) {
            this.mobileInfo.smsValidationCode = null
          } else if (!this.mobileInfo.smsValidationCode) {
            hasError = true
            textI18nKey = 'login.common.placeholder.verifyCode'
          }
        } else {
          hasError = true
          textI18nKey = 'login.common.message.getVerifyCodeFirst'
        }
      }
      if (hasError) {
        this.setErrorMessage(message, textI18nKey, getter)
        return false
      } else {
        this.clearErrorMessage(message)
        return true
      }
    },
    handelSendVerifySuccess () {
      this.hasSentVerifyCode = true
    },
    async validateChinaPhone (rule, value, callback) {
      const error = validatePhone(rule, value)
      if (error) {
        callback(this.$t(`${this.i18nPrefix}.message.mobileIsInvalid`))
      } else if (this.mobileInfo.currentMobile === value) {
        callback(this.$t('error.code.20006030'))
      }
    },
    async handleSave () {
      let result = true
      this.$refs.form.$children[0].validateField(['mobile'], (errorMsg) => {
        if (errorMsg) {
          result = false
        }
      })
      const isValid = this.validate()
      if (!result || isValid) {
        return
      }

      this.loading = true
      try {
        const params = {
          mobile: this.mobileInfo.mobile,
          sms_validation_code: this.mobileInfo.smsValidationCode
        }
        await this.updateAccountMobile(params)
        this.$message.success(this.$t(`${this.i18nPrefix}.message.success`))
        this.isVerifyCodeComponentActive = false
        this.$nextTick(() => {
          this.isVerifyCodeComponentActive = true
        })
        this.hasSentVerifyCode = false
        this.mobileInfo = {
          currentMobile: params.mobile,
          mobile: null,
          smsValidationCode: null
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.mobile-container {
  min-height: calc(100vh - var(--header-height, 124px) - 28px);
  min-height: calc(100dvh - var(--header-height, 124px) - 28px);
  background-color: #fff;

  .mobile-editor-header {
    display: flex;
    position: sticky;
    top: -14px;
    height: 52px;
    padding: 16px 0 16px 40px;
    z-index: 3;
    background-color: #fff;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.08);

    .mobile-editor-title {
      font-size: 16px;
      font-weight: 500;
      line-height: 22px;
      color: #606266;
    }
  }

  .mobile-info-container {
    width: 500px;
    margin: 80px auto 0;

    .form-line {
      min-height: 32px;
      margin-bottom: 24px;

      &.has-error {
        margin-bottom: 6px;

        .line-tip {
          padding-left: 0;
          color: #f56c6c;
        }

        .line-input {
          ::v-deep {
            > .el-input__inner {
              border-color: #f56c6c;
            }

            .verify-code {
              .el-input__inner {
                border-color: #f56c6c;
              }
            }

            .el-input__inner:hover + .el-input-group__prepend,
            .el-input__inner:hover + .el-input-group__append,
            .el-input-group__prepend,
            .el-input-group__append {
              border-color: #f56c6c;
            }
          }
        }
      }

      .line-input {
        ::v-deep {
          .el-input__inner {
            height: 32px;
            line-height: 32px;
            border-radius: 2px;
            padding-left: 14px;
            padding-right: 35px;

            &:focus {
              border-color: #0096cc;
            }
          }

          .el-input__suffix {
            right: 14px;
          }

          .el-input-group__prepend {
            border-top-left-radius: 2px;
            border-bottom-left-radius: 2px;
            background: initial;
            transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)
          }

          .el-input-group__append {
            border-top-right-radius: 2px;
            border-bottom-right-radius: 2px;
            background: initial;
            transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)
          }
        }

        .country-code {
          width: 90px;
          color: #606266;

          ::v-deep {
            .el-input {

              .el-input__inner {
                padding-right: 30px;

                &:focus {
                  border-color: transparent;
                }
              }

              .el-input__suffix {
                right: 5px;
              }
            }
          }
        }
      }

      .verify-code {
        &::v-deep {
          .el-input__inner {
            border-right: 0;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
          }

          .el-input-group__append {
            padding-left: 0;
            padding-right: 14px;
          }
        }

        .send-verify-code {
          color: #0096cc;
          cursor: pointer;

          &.has-sent {
            cursor: default;
          }
        }
      }

      .line-tip {
          padding-left: 15px;
          font-size: 12px;
          line-height: 18px;
      }
    }

    ::v-deep {
      .el-form-item {
        margin-bottom: 31px;
      }
    }

    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;
      margin: 30px auto 0;

      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;

        &:last-child {
          margin-left: 24px;
          margin-right: 0;
          background: #0096cc;
          border-color: #0096cc;
          color: #ffffff;
        }
      }
    }
  }
}
</style>
